/* tcc-native-options.js — TCC, 24 Aug 2026
 *
 * Runs only on products tagged `native-options`. Two jobs:
 *
 *  1. Remove any Webyze widget still rendering. Webyze cannot be detached per
 *     product from its admin (no unassign control, and a set refuses to drop its
 *     last option field), so a stray field always survives. CSS alone was not
 *     enough — the app renders from both an app block and an app embed.
 *
 *  2. Restore the presentation Webyze had: any two-value No/Yes option becomes a
 *     checkbox labelled with its real price delta (+$5.00 / +$20.00). The delta
 *     is read from the variant prices, so it can never drift from what is charged.
 *
 * WHY A POLL, NOT AN OBSERVER:
 * <product-variant-selector> re-renders itself on EVERY option change, destroying
 * injected nodes. A MutationObserver was tried first and still lost the checkboxes
 * when a checkbox was toggled — its rebuild raced the component's second render.
 * A 300 ms idempotent sweep is immune to that ordering and costs nothing.
 *
 * DRIVING THE THEME:
 * Each dropdown is backed by real radio inputs carrying data-variant-id. Toggling
 * calls .click() on the radio, a genuine interaction. Setting .value on the
 * #product-options-dropdown-a11y-select mirror does NOT work — the component
 * re-syncs it, which looks exactly like "the selection reverted".
 */
(function () {
  var WEBYZE = '[id*="live_preview_options_by_webyze"],' +
               '.webyze-infinitelivepreviewoptions-form,' +
               '.webyze-ilpo-option,.webyze-ilpo-preview-wrapper';
  var ITEM = '.\\#product-variant-selector-item';

  function killWebyze() {
    document.querySelectorAll(WEBYZE).forEach(function (e) { e.remove(); });
    // Webyze's option inputs are named properties['...'] and sit INSIDE the
    // product form, so anything still present at submit time is serialised onto
    // the line item. Measured 24 Aug on ELC450FX: a fast add-to-cart produced a
    // Satin Brass / Yes variant carrying "Select Metal Finish: Black" and
    // "Tapped For Signage: No" - the price was right, the order contradicted
    // itself, and the PO generator reads those properties.
    document.querySelectorAll('[name^="properties["]').forEach(function (i) {
      if (!i.closest('.tcc-optcb')) i.remove();
    });
  }

  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  function buildCheckboxes(priceById) {
    document.querySelectorAll('product-options-dropdown').forEach(function (dd) {
      var item = dd.closest(ITEM);
      // already converted and the checkbox survived the last re-render
      if (dd.dataset.tccCb && item && item.querySelector('.tcc-optcb')) return;

      var radios = [].slice.call(dd.querySelectorAll('input[type=radio]'));
      var vals = radios.map(function (r) { return r.value; });
      if (vals.length !== 2 || vals.indexOf('No') < 0 || vals.indexOf('Yes') < 0) return;

      var no  = radios.filter(function (r) { return r.value === 'No';  })[0];
      var yes = radios.filter(function (r) { return r.value === 'Yes'; })[0];
      var pNo = priceById[no.dataset.variantId], pYes = priceById[yes.dataset.variantId];
      if (pNo == null || pYes == null) return;
      var delta = pYes - pNo;

      // drop any stale checkbox left over from a previous render
      if (item) item.querySelectorAll('.tcc-optcb').forEach(function (e) { e.remove(); });

      var wrap = document.createElement('label');
      wrap.className = 'tcc-optcb';
      var box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = yes.checked;
      var txt = document.createElement('span');
      txt.innerHTML = no.name + (delta > 0 ? ' <strong>+' + money(delta) + '</strong>' : '');
      wrap.appendChild(box);
      wrap.appendChild(txt);
      box.addEventListener('change', function () { (box.checked ? yes : no).click(); });

      if (item) { item.classList.add('tcc-optcb-host'); item.appendChild(wrap); }
      else { dd.parentNode.insertBefore(wrap, dd); }
      dd.dataset.tccCb = '1';
    });
  }

  function init() {
    killWebyze();
    var h = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    if (!h) return;
    fetch('/products/' + h + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var byId = {};
        p.variants.forEach(function (v) { byId[v.id] = v.price; });
        function ensure() { patchFetch(); killWebyze(); buildCheckboxes(byId); }
        ensure();
        setInterval(ensure, 100);
      })
      .catch(function () { /* dropdowns stay as-is; nothing breaks */ });
  }

  /* Strip Webyze's line-item properties from the /cart/add request itself.
   *
   * Removing its DOM inputs is NOT enough. Measured 24 Aug on ELC450FX: with
   * zero properties[...] inputs in the form at send time, the request body was
   * still
   *   id=...&quantity=1&properties[Select Metal Finish]=Black&...
   * so the app builds the payload from its own internal state, not from the DOM.
   * The request is the only reliable place to intervene.
   *
   * Those properties are stale and contradictory on a native-variant product -
   * they said "Black / No" on a Satin Brass / Tapped variant - and the PO
   * generator reads them. Safe to drop wholesale here because this file only
   * loads on products tagged `native-options`, which carry no real properties.
   */
  function isPropKey(k) { return /^properties(\[|%5B)/i.test(String(k)); }

  function dbg(o) {
    try {
      var a = JSON.parse(sessionStorage.getItem('tccDbg') || '[]');
      a.push(o); sessionStorage.setItem('tccDbg', JSON.stringify(a.slice(-10)));
    } catch (e) {}
  }

  /* Rebuild rather than mutate.
   * Deleting keys off the caller's FormData proved unreliable in place, so a
   * clean copy is built and substituted. Duck-typed (keys/append) rather than
   * `instanceof FormData`, which fails across realms. */
  function stripProps(body) {
    if (typeof body === 'string') {
      if (body.charAt(0) === '{') {
        try {
          var j = JSON.parse(body);
          if (j.properties) delete j.properties;
          if (Array.isArray(j.items)) j.items.forEach(function (it) { delete it.properties; });
          return JSON.stringify(j);
        } catch (e) { return body; }
      }
      return body.split('&').filter(function (p) { return !isPropKey(p.split('=')[0]); }).join('&');
    }
    if (body && typeof body.keys === 'function' && typeof body.append === 'function') {
      var pairs = [];
      try { pairs = [].slice.call(body.entries()); } catch (e) { return body; }
      var kept = pairs.filter(function (kv) { return !isPropKey(kv[0]); });
      if (kept.length === pairs.length) return body;
      var out = new FormData();
      kept.forEach(function (kv) { out.append(kv[0], kv[1]); });
      dbg({ stripped: pairs.length - kept.length, kept: kept.map(function (kv) { return kv[0]; }) });
      return out;
    }
    return body;
  }

  /* Re-apply on every sweep, do NOT patch once.
   * Another app wraps window.fetch AFTER this file runs (it is deferred), which
   * silently discarded a one-shot patch. Webyze issues the /cart/add itself, so
   * ours must sit outside whatever is current at click time. */
  function patchFetch() {
    if (window.fetch && !window.fetch.__tccWrapped) {
      var inner = window.fetch;
      var wrapped = function (u, o) {
        var s = String(u && u.url ? u.url : u);
        if (s.indexOf('/cart/add') > -1) {
          if (o && o.body) {
            var nb = stripProps(o.body);
            if (nb !== o.body) { o = Object.assign({}, o, { body: nb }); }
          } else if (u && u.body && typeof Request !== 'undefined' && u instanceof Request) {
            dbg({ note: 'Request-object body, not handled', url: s });
          }
        }
        return inner.apply(this, [u, o].concat([].slice.call(arguments, 2)));
      };
      wrapped.__tccWrapped = true;
      window.fetch = wrapped;
    }
    if (!XMLHttpRequest.prototype.send.__tccWrapped) {
      var xhrOpen = XMLHttpRequest.prototype.open,
          xhrSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (m, u) { this.__tccUrl = String(u); return xhrOpen.apply(this, arguments); };
      var sendWrapped = function (b) {
        if (this.__tccUrl && this.__tccUrl.indexOf('/cart/add') > -1) b = stripProps(b);
        return xhrSend.call(this, b);
      };
      sendWrapped.__tccWrapped = true;
      XMLHttpRequest.prototype.send = sendWrapped;
    }
  }
  patchFetch();

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('button[data-to-cart]')) { patchFetch(); killWebyze(); }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
